// Rendez-vous : vue du jour (liste par heure, pointage) et vue de la semaine (sept colonnes),
// filtrées par service et par agenda ; navigation jour par jour ou semaine par semaine.
import { Bouton, EtatVide, Filtres, Lien, LienBouton, PuceEtat, Squelette, type TonEtat } from '@cc/ui';
import { CalendarDays, Settings2 } from 'lucide-react';
import { useSearchParams } from 'react-router';
import { useServices } from '../demarches/contenu';
import { EnTetePage } from '../../commun/EnTetePage';
import { AffichageErreur } from '../../commun/EtatRequete';
import { isoJour } from '../../commun/format';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { decalerJours, heure, JOURS, joursDeLaSemaine, libelleJour, LIBELLES_ETAT_RDV, lundiLocal, useAgendas, useRendezVousDeLaSemaine, useRendezVousDuJour, type FiltresRdv, type RendezVous } from './donnees';
import { PointageRendezVous } from './PointageRendezVous';
import commun from '../../commun/commun.module.css';
import styles from './rendez-vous.module.css';

function tonEtat(etat: string): TonEtat {
  if (etat === 'present') return 'ok';
  if (etat === 'absent' || etat === 'annule') return 'retard';
  if (etat === 'reporte') return 'attente';
  return 'neutre';
}

function LigneRdv({ rdv, compact }: { rdv: RendezVous; compact?: boolean }) {
  return (
    <li className={styles.rdv} data-etat={rdv.etat}>
      <span className={styles.heure}>
        {heure(rdv.debut)} à {heure(rdv.fin)}
      </span>
      <span className={styles.corpsRdv}>
        <strong>{rdv.demandeur.nom ?? 'Usager'}</strong>
        <span className={commun.na}>
          {rdv.agenda.libelle}
          {rdv.agenda.lieu ? `, ${rdv.agenda.lieu}` : ''}
          {rdv.objet ? ` : ${rdv.objet}` : ''}
          {rdv.dossier_id ? (
            <>
              {' '}
              <Lien href={`/dossiers/${rdv.dossier_id}`}>dossier lié</Lien>
            </>
          ) : null}
        </span>
      </span>
      <PuceEtat ton={tonEtat(rdv.etat)} libelle={LIBELLES_ETAT_RDV[rdv.etat] ?? rdv.etat} />
      <PointageRendezVous rdv={rdv} compact={compact} />
    </li>
  );
}

function VueJour({ date, filtres }: { date: string; filtres: FiltresRdv }) {
  const jour = useRendezVousDuJour(date, filtres);
  if (jour.isPending) return <Squelette lignes={4} />;
  if (jour.error) return <AffichageErreur erreur={jour.error} lot="L1-A" fonction="La liste des rendez-vous" />;
  const actifs = jour.data.elements.filter((r) => r.etat === 'confirme' || r.etat === 'reserve').length;
  return (
    <div className={commun.pile}>
      <p role="status">
        {jour.data.elements.length} rendez-vous le {libelleJour(date)}, dont {actifs} à pointer.
      </p>
      {jour.data.elements.length === 0 ? (
        <EtatVide titre="Aucun rendez-vous ce jour" icone={CalendarDays}>
          <p>Aucun rendez-vous pris pour ces filtres.</p>
        </EtatVide>
      ) : (
        <ol className={styles.liste} aria-label={`Rendez-vous du ${libelleJour(date)}`}>
          {jour.data.elements.map((r) => (
            <LigneRdv key={r.id} rdv={r} />
          ))}
        </ol>
      )}
    </div>
  );
}

function VueSemaine({ lundi, filtres }: { lundi: string; filtres: FiltresRdv }) {
  const jours = joursDeLaSemaine(lundi);
  const resultats = useRendezVousDeLaSemaine(lundi, filtres);
  const aujourdhui = isoJour(new Date());
  return (
    <ol className={styles.semaine} aria-label={`Semaine du ${libelleJour(lundi)}`}>
      {jours.map((date, i) => {
        const r = resultats[i];
        return (
          <li key={date} className={styles.jour} aria-current={date === aujourdhui ? 'date' : undefined}>
            <h3>
              {JOURS[i]} <span className="cc-mono">{date.slice(8, 10)}/{date.slice(5, 7)}</span>
            </h3>
            {r?.isPending ? <Squelette lignes={2} /> : r?.error ? <p className={commun.na}>Non disponible</p> : (r?.data?.elements.length ?? 0) === 0 ? <p className={commun.na}>Rien de prévu</p> : (
              <ol className={styles.liste}>
                {r?.data?.elements.map((x) => (
                  <LigneRdv key={x.id} rdv={x} compact />
                ))}
              </ol>
            )}
          </li>
        );
      })}
    </ol>
  );
}

export function PageRendezVous({ vue }: { vue: 'jour' | 'semaine' }) {
  const { contexte } = useSession();
  const [params, setParams] = useSearchParams();
  const date = params.get('date') ?? isoJour(new Date());
  const filtres: FiltresRdv = { service: params.get('service') ?? '', agenda: params.get('agenda') ?? '' };
  const services = useServices();
  const agendas = useAgendas();
  const changer = (patch: Record<string, string>) =>
    setParams((p) => {
      for (const [k, v] of Object.entries(patch)) {
        if (v) p.set(k, v);
        else p.delete(k);
      }
      return p;
    });
  const lundi = lundiLocal(date);
  const pas = vue === 'jour' ? 1 : 7;
  return (
    <>
      <EnTetePage
        etiquette="Rendez-vous"
        titre={vue === 'jour' ? libelleJour(date) : `Semaine du ${libelleJour(lundi)}`}
        description="Rendez-vous pris par les usagers sur les agendas des services. Pointez la présence, l'absence ou reportez sur un autre créneau."
        actions={
          <>
            <LienBouton href={`/rendez-vous${vue === 'jour' ? '/semaine' : ''}?${params.toString()}`} icone={CalendarDays}>
              {vue === 'jour' ? 'Vue de la semaine' : 'Vue du jour'}
            </LienBouton>
            {peut(contexte, 'parametres', 'voir') ? (
              <LienBouton href="/rendez-vous/agendas" icone={Settings2}>
                Agendas
              </LienBouton>
            ) : null}
          </>
        }
      />
      <div className={commun.ligne}>
        <Bouton variante="secondaire" petit onClick={() => changer({ date: decalerJours(date, -pas) })}>
          {vue === 'jour' ? 'Jour précédent' : 'Semaine précédente'}
        </Bouton>
        <Bouton variante="secondaire" petit onClick={() => changer({ date: isoJour(new Date()) })}>
          Aujourd&apos;hui
        </Bouton>
        <Bouton variante="secondaire" petit onClick={() => changer({ date: decalerJours(date, pas) })}>
          {vue === 'jour' ? 'Jour suivant' : 'Semaine suivante'}
        </Bouton>
        <label className={styles.date}>
          Date
          <input type="date" value={date} onChange={(e) => e.target.value && changer({ date: e.target.value })} />
        </label>
      </div>
      <Filtres
        libelle="Filtres des rendez-vous"
        groupes={[
          { id: 'service', libelle: 'Service', options: [{ valeur: '', libelle: 'Tous' }, ...(services.data ?? []).map((s) => ({ valeur: s.id, libelle: s.nom }))] },
          { id: 'agenda', libelle: 'Agenda', options: [{ valeur: '', libelle: 'Tous' }, ...(agendas.data ?? []).map((a) => ({ valeur: a.id, libelle: a.libelle }))] },
        ]}
        valeurs={{ ...filtres }}
        onChanger={(v) => changer(v)}
      />
      {vue === 'jour' ? <VueJour date={date} filtres={filtres} /> : <VueSemaine lundi={lundi} filtres={filtres} />}
    </>
  );
}
